import { ApolloCache, Cache, DataProxy, FieldFunctionOptions, NormalizedCacheObject, Reference, Unmasked } from "@apollo/client";
import { OverlayList } from "./list";

interface FragmentCache {
  data: any
  fragment: any
  fragmentName: string | undefined
}

export interface AddOrUpdateOption<TData, TVariables> extends Omit<DataProxy.Fragment<TVariables, TData>, "id"> {
  data: TData;
}

export interface FieldFunctionOptionsEx extends FieldFunctionOptions {
  rowVersionField?: string
}

export class OverlayContext {
  _fragmentCache: Record<string, FragmentCache> = {}
  _counter = 0;

  addOrUpdate<TData = any, TVariables = any>(cacheOrId: ApolloCache<NormalizedCacheObject> | string, options: AddOrUpdateOption<TData, TVariables>, lists: OverlayList<TData, TVariables>[], updateCache: (id: string | undefined) => any): Reference | undefined {
    const id = typeof cacheOrId == "string" ? cacheOrId : cacheOrId.identify(options.data as any);
    if (id) {
      this._fragmentCache[id] = {
        data: options.data,
        fragment: options.fragment,
        fragmentName: options.fragmentName
      }
    }
    try {
      ++this._counter;
      updateCache(id);
      if (id) {
        for (let list of lists) {
          list.add(id);
        }
      }
    }
    finally {
      --this._counter;
    }

    return id ? {__ref: id} : undefined;
  }

  remove<TData = any, TVariables = any>(id: string | undefined, rowVersion: string | null | undefined, lists: OverlayList<TData, TVariables>[], updateCache?: () => any) {
    try {
      ++this._counter;

      if (updateCache)
        updateCache();

      if (id) {
        if (id in this._fragmentCache) {
          delete this._fragmentCache[id];
        }
        for (let list of lists) {
          list.remove(id, rowVersion);
        }
      }
    }
    finally {
      --this._counter;
    }
  }

  writeFragment<TData = any, TVariables = any>(
    cache: ApolloCache<NormalizedCacheObject>,
    options: Cache.WriteFragmentOptions<TData, TVariables>,
    ...lists: OverlayList<Unmasked<TData>, TVariables>[]
  ): Reference | undefined {
    return this.addOrUpdate(cache, options, lists, () => {
      cache.writeFragment(options);
    })
  }

  updateFragment<TData = any, TVariables = any>(
    cache: ApolloCache<NormalizedCacheObject>,
    options: Cache.UpdateFragmentOptions<TData, TVariables>,
    update: (data: Unmasked<TData> | null) => Unmasked<TData> | null | void,
    ...lists: OverlayList<Unmasked<TData>, TVariables>[]
  ): Unmasked<TData> | null {
    try {
      ++this._counter;
      const data = cache.updateFragment(options, update);
      const id = options.id ?? cache.identify(data as any);
      if (id) {
        this._fragmentCache[id] = {
          data: data,
          fragment: options.fragment,
          fragmentName: options.fragmentName
        }
        for (var list of lists) {
          list.add(id);
        }
      }
      return data;
    }
    finally {
      --this._counter;
    }
  }

  mergeObjectsInternal = (incoming: any, options: FieldFunctionOptionsEx) => {
    const id = options.cache.identify(incoming as any);
    if (!id || !(id in this._fragmentCache)) {
      return;
    }

    const fragment = this._fragmentCache[id];
    const rowVersionField = options.rowVersionField ?? "rowVersion";

    if (!(rowVersionField in fragment.data)) {
      delete this._fragmentCache[id];
      return;
    }

    const currentVersion = fragment.data[rowVersionField];
    const newVersion = options.readField<string>(rowVersionField, incoming);

    if (newVersion &&
      (!currentVersion || Number.parseInt(newVersion) > Number.parseInt(currentVersion))
    ) {
      delete this._fragmentCache[id];
      return;
    }

    options.cache.writeFragment({
      id: id,
      data: fragment.data,
      fragment: fragment.fragment,
      fragmentName: fragment.fragmentName,
      broadcast: false
    });
  }

  mergeObjects = (_existing: any, incoming: any, options: FieldFunctionOptionsEx) => {
    if (this._counter > 0)
      return incoming;

    if (Array.isArray(incoming)) {
      for (var item of incoming) {
        this.mergeObjectsInternal(item, options);
      }
    }
    else {
      this.mergeObjectsInternal(incoming, options);
    }

    return incoming;
  }
}